// Readable text helpers: skill names get their card colour and glyph, mythos cards get a
// structured layout, and decision prompts put "what the result means" on separate lines.
import { Fragment, type ReactNode } from "react";

import * as uf from "../../../../common/unfathomable";
import css from "./Unfathomable.module.css";

/** Dark enough to read on the tags' light pill, whatever is behind it. */
export const kSkillColor: Record<uf.SkillType, string> = {
  influence: "#b0521a", lore: "#7438ad", observation: "#25744a", strength: "#ab2222", will: "#2c56a6", treachery: "#444444",
};
const kSkillGlyph: Record<uf.SkillType, string> = {
  influence: "♚", lore: "✠", observation: "◉", strength: "✊", will: "✶", treachery: "☠",
};

const kZhToSkill = new Map<string, uf.SkillType>(
  (Object.entries(uf.kSkillNameZh) as [uf.SkillType, string][]).map(([t, zh]) => [zh, t]),
);
const kSkillRe = new RegExp(`(${[...kZhToSkill.keys()].join("|")})`, "g");

export function SkillTag({ type, children }: { type: uf.SkillType; children?: ReactNode }) {
  return (
    <span className={css.skillTag} style={{ color: kSkillColor[type], borderColor: kSkillColor[type] }}>
      <span className={css.skillGlyph}>{kSkillGlyph[type]}</span>{children ?? uf.kSkillNameZh[type]}
    </span>
  );
}

/** Plain text with every skill name (影响 / 学识 / 观察 / 力量 / 意志 / 背叛) highlighted. */
export function Rich({ text }: { text: string }) {
  const parts = text.split(kSkillRe);
  return (
    <>
      {parts.map((p, i) => {
        const t = kZhToSkill.get(p);
        return t ? <SkillTag key={i} type={t} /> : <Fragment key={i}>{p}</Fragment>;
      })}
    </>
  );
}

/** Decision prompts: "question｜note｜note" → the question, then each note on its own line. */
export function PromptText({ text }: { text: string }) {
  const [head, ...notes] = text.split("｜");
  return (
    <>
      <Rich text={head} />
      {notes.map((n, i) => <div key={i} className={css.promptNote}>🎲 <Rich text={n} /></div>)}
    </>
  );
}

// ---------------------------------------------------------------- mythos
const kActivationZh: Record<uf.MonsterActivation, string> = { hydra: "母神海德拉激活", dagon: "父神大衮激活", deepOnes: "深潜者激活" };
const kTrackZh: Record<uf.TrackIcon, string> = { travel: "航迹 +1", ritual: "仪式轨 +1", choice: "航迹或仪式轨 +1（当前玩家选择）" };

function outcomeClass(label: string): string {
  if (label === "通过") return css.outPass;
  if (label === "失败") return css.outFail;
  return css.outPartial;
}

/** Split "通过：… / 8+：… / 失败：…" into labelled rows; text before the first label stays as a lead-in. */
function outcomeRows(text: string): { label: string | null; body: string }[] {
  const pieces = text.split(/(?<!\d)(?=(?:通过|失败|\d+\+)：)/);
  return pieces
    .map(p => p.replace(/^[\s/；;。，]+|[\s/；;。，]+$/g, ""))
    .filter(Boolean)
    .map(p => {
      const m = p.match(/^(通过|失败|\d+\+)：(.*)$/s);
      return m ? { label: m[1], body: m[2] } : { label: null, body: p };
    });
}

function Outcomes({ text }: { text: string }) {
  return (
    <>
      {outcomeRows(text).map((r, i) => (
        <div key={i} className={css.mythosRow}>
          {r.label && <span className={`${css.outTag} ${outcomeClass(r.label)}`}>{r.label === "通过" ? "✔ 通过" : r.label === "失败" ? "✘ 失败" : `◐ ${r.label}`}</span>}
          <span><Rich text={r.body} /></span>
        </div>
      ))}
    </>
  );
}

export function chooserZh(c: string): string {
  if (c === "captain") return "船长";
  if (c === "keeper") return "典籍守护者";
  if (c === "current") return "当前玩家";
  return uf.kCharacters[c as uf.CharacterId]?.name ?? c;
}

/** A mythos card laid out as: check line, outcomes / options, then activation and track icons. */
export function MythosText({ id, compact }: { id: string; compact?: boolean }) {
  const m = uf.kMythosById[id];
  const options = m.text.match(/^A：/) ? m.text.split(/(?=B：)/).map(x => x.replace(/^[AB]：/, "")) : null;
  return (
    <div className={`${css.mythosText} ${compact ? css.mythosTextCompact : ""}`}>
      {m.check && (
        <div className={css.mythosCheck}>
          检定 <b>{m.check.target}</b>{m.check.partial !== undefined && <>（部分通过 {m.check.partial}+）</>}
          {" "}{m.check.supporting.map(t => <SkillTag key={t} type={t} />)}
        </div>
      )}
      {m.chooser && m.chooser !== "current" && <div className={css.mythosChooser}>由 <b>{chooserZh(m.chooser)}</b> 决定</div>}
      {options ? options.map((o, i) => (
        <div key={i} className={css.mythosOption}>
          <div className={css.mythosOptionHead}>{i === 0 ? "A" : "B"}{m.options?.[i] ? ` · ${m.options[i]}` : ""}</div>
          <Outcomes text={o} />
        </div>
      )) : <Outcomes text={m.text} />}
      <div className={css.mythosIcons}>
        <span>☠ {kActivationZh[m.activation]}</span>
        <span>⚑ {kTrackZh[m.track]}</span>
      </div>
    </div>
  );
}
